import { useMemo, useState } from 'react';
import styles from './UploadNewVersionModal.module.scss';
import { Button, ButtonColor } from 'components/uikit/buttons/Button';
import { Field } from 'components/uikit/Field';
import { TextArea } from 'components/uikit/inputs/TextArea';
import { useScopedTranslation } from 'application/localization/useScopedTranslation';
import { Trans } from 'react-i18next';
import CrossIcon from 'assets/icons/cross_12.svg?react';
import { DocumentData } from 'helpers/documents/fileHandler';
import { Loading } from 'components/uikit/suspense/Loading';
import Dropzone from 'components/uikit/Dropzone';
import { maxLengthRule } from 'helpers/form/react-hook-form-helper';
import { useAdvancedForm } from 'helpers/form/useAdvancedForm';
import { showErrorToast } from 'components/uikit/toast/showToast';

type UploadNewVersionModalProps = {
  templateName: string;
  isOpen: boolean;
  onClose: () => void;
  onCreate: (template: DocumentData, comment: string) => Promise<void>;
};

export const maxCommentLength = 5000;

export const UploadNewVersionModal: React.FC<UploadNewVersionModalProps> = (
  props,
) => {
  const i18n = useScopedTranslation('Component.UploadNewVersionModal');
  const [isLoading, setIsLoading] = useState(false);

  const form = useAdvancedForm<{ comment: string; file: File | undefined }>(
    async (data) => {
      if (data.file) {
        await onSubmit(data as { comment: string; file: File });
      }
      form.reset();
      props.onClose();
    },
    {
      defaultValues: useMemo(() => {
        return { comment: '', file: undefined };
      }, []),
    },
  );

  const file = form.watch('file');

  const onSubmit = async (data: { comment: string; file: File }) => {
    setIsLoading(true);

    const readPromise = new Promise<DocumentData>((resolveFile) => {
      const reader = new FileReader();
      reader.readAsDataURL(data.file);

      reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.split(',')[1];
        resolveFile({ name: data.file.name, content: base64 });
      };

      reader.onerror = (_) => {
        showErrorToast('Unable to open file');
      };
    });

    const resolvedFile = await readPromise;
    await props.onCreate(resolvedFile, data.comment);
    setIsLoading(false);
  };

  return (
    <Loading loading={isLoading}>
      <form onSubmit={form.handleSubmitDefault}>
        <div className={styles.container}>
          <div className={styles.body}>
            <p className={styles.text}>
              <Trans
                i18nKey={'Component.UploadNewVersionModal.text'}
                values={{
                  templateName: props.templateName,
                }}
                components={{
                  bold: <strong />,
                }}
              />
            </p>

            {file ? (
              <div className={styles.uploadedContainer}>
                <div className={styles.fileContainer}>
                  <div className={styles.file}>
                    <p className={styles.fileName}>{file.name}</p>
                    <p className={styles.fileInfo}>
                      {formatFileSize(file.size)}
                    </p>
                  </div>
                  <CrossIcon
                    className={styles.crossIcon}
                    onClick={() => form.setValue('file', undefined)}
                  />
                </div>
                <Field title={i18n.t('comment_field')}>
                  <TextArea
                    {...form.register('comment', {
                      ...maxLengthRule(maxCommentLength),
                    })}
                    className={styles.commentArea}
                    placeholder={i18n.t('comment_placeholder')}
                    errorText={form.formState.errors.comment?.message}
                  />
                </Field>
              </div>
            ) : (
              <Dropzone
                className={styles.dropzone}
                multiple={false}
                onDropAccepted={(files: File[]) =>
                  form.setValue('file', files[0])
                }
                accept={new Set(['.docx', '.dotx'])}
              />
            )}
          </div>

          <div className={styles.footer}>
            <Button
              className={styles.button}
              color={ButtonColor.Secondary}
              title={i18n.t('cancel_button')}
              onClick={props.onClose}
            />
            <Button
              disabled={!file}
              className={styles.button}
              color={ButtonColor.Primary}
              type={'submit'}
              title={i18n.t('save_button')}
            />
          </div>
        </div>
      </form>
    </Loading>
  );
};

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const k = 1024;

  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const size = bytes / Math.pow(k, i);

  return `${size.toFixed(size < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
}
